/**
 * Input abstraction shared by the web app and the Android key bridge.
 *
 * Standard D-pad arrows, Enter and digits arrive in a WebView as ordinary
 * KeyboardEvents. Everything else a TV remote sends -- BACK, CHANNEL_UP/DOWN,
 * the coloured buttons, the media transport keys -- never reaches JS, so the
 * Kotlin Activity intercepts those and re-emits them as a `remotekey`
 * CustomEvent carrying one of these `RemoteKey` names.
 */
export const REMOTE_KEYS = [
    'Up', 'Down', 'Left', 'Right', 'Ok', 'Back', 'Menu', 'Info',
    'ChannelUp', 'ChannelDown',
    'PlayPause', 'Stop', 'Mute',
    'Red', 'Green', 'Yellow', 'Blue',
    'Digit0', 'Digit1', 'Digit2', 'Digit3', 'Digit4',
    'Digit5', 'Digit6', 'Digit7', 'Digit8', 'Digit9',
];
/** Android KeyEvent keycode -> RemoteKey. Mirrored in the Kotlin bridge. */
export const ANDROID_KEYCODE_TO_REMOTE = {
    19: 'Up', 20: 'Down', 21: 'Left', 22: 'Right',
    23: 'Ok', 66: 'Ok',
    4: 'Back', 82: 'Menu', 165: 'Info',
    166: 'ChannelUp', 167: 'ChannelDown',
    85: 'PlayPause', 126: 'PlayPause', 127: 'PlayPause',
    86: 'Stop', 164: 'Mute',
    183: 'Red', 184: 'Green', 185: 'Yellow', 186: 'Blue',
    7: 'Digit0', 8: 'Digit1', 9: 'Digit2', 10: 'Digit3', 11: 'Digit4',
    12: 'Digit5', 13: 'Digit6', 14: 'Digit7', 15: 'Digit8', 16: 'Digit9',
};
/** KeyboardEvent.key -> RemoteKey, so a desktop keyboard drives the same code. */
export const KEYBOARD_KEY_TO_REMOTE = {
    ArrowUp: 'Up', ArrowDown: 'Down', ArrowLeft: 'Left', ArrowRight: 'Right',
    Enter: 'Ok', ' ': 'PlayPause',
    Escape: 'Back', Backspace: 'Back',
    m: 'Mute', M: 'Mute',
    i: 'Info', I: 'Info',
    PageUp: 'ChannelUp', PageDown: 'ChannelDown',
    F1: 'Red', F2: 'Green', F3: 'Yellow', F4: 'Blue',
    '0': 'Digit0', '1': 'Digit1', '2': 'Digit2', '3': 'Digit3', '4': 'Digit4',
    '5': 'Digit5', '6': 'Digit6', '7': 'Digit7', '8': 'Digit8', '9': 'Digit9',
};
const SYNC_STEP_MS = 500;
export function resolveAction(key, mode) {
    const digit = DIGITS[key];
    if (digit !== undefined) {
        return mode === 'watch' ? { type: 'angle.select', index: digit } : null;
    }
    switch (key) {
        case 'Back':
            return { type: 'back' };
        case 'Ok':
            return mode === 'watch' ? { type: 'overlay.toggle' } : { type: 'activate' };
        case 'Menu':
            return { type: 'overlay.toggle' };
        case 'Info':
            return { type: 'info.toggle' };
        case 'ChannelUp':
            return { type: 'angle.cycle', delta: 1 };
        case 'ChannelDown':
            return { type: 'angle.cycle', delta: -1 };
        case 'PlayPause':
            return { type: 'playPause' };
        case 'Mute':
            return { type: 'audio.toggleMute' };
        case 'Red':
            return { type: 'hud.toggle' };
        case 'Green':
            return { type: 'audio.pinFocused' };
        case 'Yellow':
            return { type: 'sync.nudge', deltaMs: -SYNC_STEP_MS };
        case 'Blue':
            return { type: 'sync.nudge', deltaMs: SYNC_STEP_MS };
    }
    if (mode === 'watch') {
        switch (key) {
            case 'Left':
                return { type: 'angle.cycle', delta: -1 };
            case 'Right':
                return { type: 'angle.cycle', delta: 1 };
            case 'Up':
                return { type: 'layout.cycle', delta: -1 };
            case 'Down':
                return { type: 'layout.cycle', delta: 1 };
        }
    }
    else {
        switch (key) {
            case 'Up':
                return { type: 'nav', dir: 'up' };
            case 'Down':
                return { type: 'nav', dir: 'down' };
            case 'Left':
                return { type: 'nav', dir: 'left' };
            case 'Right':
                return { type: 'nav', dir: 'right' };
        }
    }
    return null;
}
const DIGITS = {
    Digit0: 0, Digit1: 1, Digit2: 2, Digit3: 3, Digit4: 4,
    Digit5: 5, Digit6: 6, Digit7: 7, Digit8: 8, Digit9: 9,
};
//# sourceMappingURL=keys.js.map